"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { COUNTRIES, COUNTRY_CONFIG, type CountryCode } from "@/lib/countries";
import type { Address } from "@/lib/schemas/paycode/address";
import { useCheckoutStore } from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import { ContinueButton } from "./continue-button";
import { UseShippingCard } from "./use-shipping-card";

type AddressTranslator = (key: string) => string;

const makeAddressSchema = (t: AddressTranslator) =>
  z
    .object({
      country: z.enum(["MX", "AR", "CO"], {
        message: t("errors.countryRequired"),
      }),
      addressLine1: z.string().min(1, t("errors.addressRequired")),
      addressLine2: z.string().optional(),
      state: z.string(),
      city: z.string().min(1, t("errors.cityRequired")),
      zipCode: z.string().min(1, t("errors.postalCodeRequired")),
    })
    .superRefine((data, ctx) => {
      const config = COUNTRY_CONFIG[data.country];
      if (!config) return;

      if (!config.states.includes(data.state)) {
        ctx.addIssue({
          code: "custom",
          path: ["state"],
          message: t(`errors.stateRequired${data.country}`),
        });
      }

      if (!config.postalRegex.test(data.zipCode)) {
        ctx.addIssue({
          code: "custom",
          path: ["zipCode"],
          message: t(`errors.postalError${data.country}`),
        });
      }
    });

export type AddressData = z.infer<ReturnType<typeof makeAddressSchema>>;

const toAddress = (data: AddressData, id?: number): Address => ({
  id,
  addressLine1: data.addressLine1.trim(),
  addressLine2: data.addressLine2?.trim() ?? "",
  city: data.city.trim(),
  state: data.state,
  zipCode: data.zipCode.trim().toUpperCase(),
  country: data.country,
});

const triggerClassName = "min-h-12 w-full rounded-lg";
const errorTextClassName = "ml-2 text-destructive text-sm";

const formatAddressOption = (address: Address) =>
  [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

export const AddressForm = () => {
  const t = useTranslations("address");
  const tc = useTranslations("common");
  const setShipping = useCheckoutStore((s) => s.setShipping);
  const setBilling = useCheckoutStore((s) => s.setBilling);
  const completeCurrentStep = useCheckoutStore((s) => s.completeCurrentStep);
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const existing = useCheckoutStore((s) =>
    s.currentStep === "shipping" ? s.shippingAddress : s.billingAddress,
  );
  const savedAddresses = useCheckoutStore((s) =>
    s.currentStep === "shipping"
      ? s.shippingAddressOptions
      : s.billingAddressOptions,
  );

  const setAddress = currentStep === "shipping" ? setShipping : setBilling;

  const defaultCountry: CountryCode =
    existing?.country && existing.country in COUNTRY_CONFIG
      ? (existing.country as CountryCode)
      : "MX";

  const schema = useMemo(() => makeAddressSchema(t), [t]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AddressData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: {
      country: defaultCountry,
      addressLine1: existing?.addressLine1 ?? "",
      addressLine2: existing?.addressLine2 ?? "",
      state: existing?.state ?? "",
      city: existing?.city ?? "",
      zipCode: existing?.zipCode ?? "",
    },
  });

  const country = watch("country");
  const config = COUNTRY_CONFIG[country];

  const onSubmit = (data: AddressData) => {
    setAddress(toAddress(data, existing?.id));
    completeCurrentStep();
  };

  const handleSelectSaved = (value: string) => {
    const selected = savedAddresses?.[Number(value)];
    if (!selected) return;
    setAddress(selected);
    completeCurrentStep();
  };

  const hasSavedAddresses = !!savedAddresses && savedAddresses.length > 0;

  return (
    <div className="flex flex-col gap-4 pb-4">
      {currentStep === "billing" && (
        <>
          <UseShippingCard />
          <div className="h-px w-full bg-border/20" />
        </>
      )}
      {hasSavedAddresses && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label className="ml-2">{t("savedAddresses")}</Label>
            <Select onValueChange={handleSelectSaved}>
              <SelectTrigger className={triggerClassName}>
                <SelectValue placeholder={t("selectAddress")} />
              </SelectTrigger>
              <SelectContent>
                {savedAddresses.map((address, index) => (
                  <SelectItem key={address.id ?? index} value={String(index)}>
                    {formatAddressOption(address)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="h-px w-full bg-border/20" />
          <p className="ml-2 text-muted text-sm">{t("orEnterNew")}</p>
        </>
      )}

      <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1.5">
          <Label className="ml-2" htmlFor="country">
            {t("country")}
          </Label>
          <Controller
            control={control}
            name="country"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue("state", "");
                }}
              >
                <SelectTrigger
                  id="country"
                  className={triggerClassName}
                  aria-invalid={!!errors.country}
                >
                  <SelectValue placeholder={t("selectCountry")} />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.country ? (
            <p className={errorTextClassName}>{errors.country.message}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="ml-2" htmlFor="addressLine1">
            {t("addressLine1")}
          </Label>
          <Controller
            control={control}
            name="addressLine1"
            render={({ field }) => (
              <Input
                id="addressLine1"
                placeholder={t("addressLine1Placeholder")}
                autoComplete="address-line1"
                aria-invalid={!!errors.addressLine1}
                {...field}
              />
            )}
          />
          {errors.addressLine1 ? (
            <p className={errorTextClassName}>{errors.addressLine1.message}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="ml-2" htmlFor="addressLine2">
            {t("addressLine2")}
          </Label>
          <Controller
            control={control}
            name="addressLine2"
            render={({ field }) => (
              <Input
                id="addressLine2"
                placeholder={t("addressLine2Placeholder")}
                autoComplete="address-line2"
                {...field}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label className="ml-2" htmlFor="state">
              {t(`state${country}`)}
            </Label>
            <Controller
              control={control}
              name="state"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="state"
                    className={triggerClassName}
                    aria-invalid={!!errors.state}
                  >
                    <SelectValue
                      placeholder={tc("selectPlaceholder", {
                        name: t(`state${country}`).toLowerCase(),
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {config.states.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.state ? (
              <p className={errorTextClassName}>{errors.state.message}</p>
            ) : null}
          </div>

          <div className="flex flex-1 flex-col gap-1.5">
            <Label className="ml-2" htmlFor="city">
              {t("city")}
            </Label>
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <Input
                  id="city"
                  placeholder={t("city")}
                  autoComplete="address-level2"
                  aria-invalid={!!errors.city}
                  {...field}
                />
              )}
            />
            {errors.city ? (
              <p className={errorTextClassName}>{errors.city.message}</p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="ml-2" htmlFor="zipCode">
            {t("postalCode")}
          </Label>
          <Controller
            control={control}
            name="zipCode"
            render={({ field }) => (
              <Input
                id="zipCode"
                placeholder={t("postalCode")}
                autoComplete="postal-code"
                aria-invalid={!!errors.zipCode}
                {...field}
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
              />
            )}
          />
          {errors.zipCode ? (
            <p className={errorTextClassName}>{errors.zipCode.message}</p>
          ) : null}
        </div>

        <motion.div
          layout="position"
          layoutId="continue-button"
          className="self-center"
          transition={{ duration: 0.16, ease: "easeOut" }}
        >
          <ContinueButton />
        </motion.div>
      </form>
    </div>
  );
};

const FieldSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <Skeleton className="ml-2 h-4 w-20" />
    <Skeleton className="h-12 w-full rounded-lg" />
  </div>
);

export const AddressFormSkeleton = ({
  savedAddresses = false,
}: {
  savedAddresses?: boolean;
}) => (
  <div className="flex flex-col gap-4 pb-4">
    {savedAddresses && (
      <>
        <FieldSkeleton />
        <div className="h-px w-full" />
        <Skeleton className="ml-2 h-4 w-40" />
      </>
    )}
    <div className="flex flex-col gap-2">
      <FieldSkeleton />
      <FieldSkeleton />
      <FieldSkeleton />
      <div className="flex flex-col gap-2 md:flex-row">
        <FieldSkeleton className="flex-1" />
        <FieldSkeleton className="flex-1" />
      </div>
      <FieldSkeleton />
      <Skeleton className="mt-4 h-12 w-36 self-center rounded-full" />
    </div>
  </div>
);
